import React, { ChangeEvent, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Dropdown,
  Flag,
  Icon,
  Switch,
  Text,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { CurrencyInputWithSymbol, Currency } from '@vend/business-components'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import { useAppSelector } from '../../../state/hooks'
import {
  selectCustomer,
  selectSaleFinals,
} from '../../../state/slices/currentSale/currentSaleSlice'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { buildRetailerDetails } from '../../../functions/invoicing/payment-request-resources'
import { validateMerchantAccount } from '../../../functions/invoicing/validate-merchant-account'
import { getMinimumLsPayAmount } from '../../../util/lspay-utils'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { FEATURE_ENABLE_INVOICING_PAYMENT_REQUEST_QUOTE_DELIVERY } from '../../../constants'
import { PaymentRequestModalWrapper } from '../payment-panel/PaymentRequest/PaymentRequestModalWrapper'
import { useShowPaymentRequestButton } from '../payment-panel/PaymentRequest/hooks/useShowPaymentRequestButton'
import { openSaleAddressModal } from '../sale-line-item/fulfilment/modals/useSaleAddressModal'
import { EditAddressButtons } from '../sale-line-item/fulfilment/address/EditAddressButton'
import {
  getCustomerAddressDetails,
  isAllAddressFieldsEmpty,
} from '../sale-line-item/fulfilment/popover/utils/addressHelper'
import { useLocalizedAddress } from '../sale-line-item/fulfilment/popover/hooks/useLocalizedAddress'
import { FulfilmentType } from '../../../models/fulfilment'

const TOGGLE_CONTEXT = 'Quote payment request toggle label'
const AMOUNT_CONTEXT = 'Quote payment request amount section'
const INFO_CONTEXT = 'Quote payment request info message'
const NOTE_CONTEXT = 'Quote payment request note input'
const NEW_CONTEXT = 'Quote payment request new badge'
const CONVERT_TO_CONTEXT = 'Quote payment request convert to'
const ADDRESS_CONTEXT = 'Quote payment request address section'

type ConvertToType = 'delivery' | 'pickup'

interface ConvertToOption {
  id: ConvertToType
  name: string
}

const CONVERT_TO_OPTIONS: ConvertToOption[] = [
  { id: 'delivery', name: 'Unfulfilled / Delivery' },
  { id: 'pickup', name: 'Unfulfilled / Pickup' },
]

export interface QuotePaymentRequestState {
  isToggleEnabled: boolean
  amount: string
  note: string
  email: string
  isShareLink: boolean
  deliveryAddress?: string
  deliveryAddressDetails?: AddressDetails
  billingAddress?: string
  billingAddressDetails?: AddressDetails
  convertTo: ConvertToType
}

const isSameAddress = (
  a: AddressDetails | undefined,
  b: AddressDetails | undefined
): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

interface QuotePaymentRequestCardProps {
  state: QuotePaymentRequestState
  onChange: (
    stateOrUpdater:
      | QuotePaymentRequestState
      | ((prev: QuotePaymentRequestState) => QuotePaymentRequestState)
  ) => void
}

export const QuotePaymentRequestCard: React.FC<QuotePaymentRequestCardProps> = ({
  state,
  onChange,
}) => {
  const t = useT()
  const [showEditModal, setShowEditModal] = useState(false)
  const [addressDetails, setAddressDetails] = useState<
    AddressDetails | undefined
  >(undefined)
  const customer = useAppSelector(selectCustomer)
  const saleFinals = useAppSelector(selectSaleFinals)
  const saleTotal = Number(saleFinals.inclusivePrice || 0)
  const customerEmail = customer?.email || ''
  const currentOutlet = useRegisterManager().getActiveOutlet()
  const retailer = useRetailerSettingsManager().get()
  const { minimumAmount } = getMinimumLsPayAmount(currentOutlet)
  const formatLocalizedAddress = useLocalizedAddress()
  const isQuoteDeliveryEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_REQUEST_QUOTE_DELIVERY
  )

  const { showButton } = useShowPaymentRequestButton({
    amountTendered: state.amount || saleTotal.toFixed(2),
    hasPaymentAmountError: false,
  })

  useEffect(() => {
    if (customerEmail && !state.email) {
      onChange(prev => ({ ...prev, email: customerEmail }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail])

  useEffect(() => {
    if (state.isToggleEnabled && !state.amount) {
      onChange(prev => ({ ...prev, amount: saleTotal.toFixed(2) }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isToggleEnabled])

  useEffect(() => {
    if (
      isQuoteDeliveryEnabled &&
      customer &&
      state.convertTo === 'delivery' &&
      !state.deliveryAddressDetails
    ) {
      const customerShipping = getCustomerAddressDetails(
        customer,
        AddressTypes.Shipping
      )

      if (!isAllAddressFieldsEmpty(customerShipping)) {
        setAddressDetails(customerShipping)
        onChange(prev => ({
          ...prev,
          deliveryAddress: formatLocalizedAddress(customerShipping),
          deliveryAddressDetails: customerShipping,
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, isQuoteDeliveryEnabled, state.convertTo])

  const handleToggle = (e: ChangeEvent<HTMLInputElement>) => {
    const isToggleEnabled = e.target.checked
    onChange({
      ...state,
      isToggleEnabled,
      amount: isToggleEnabled ? saleTotal.toFixed(2) : '',
      isShareLink: state.isShareLink,
    })
  }

  const handleAmountChange = (value: number) => {
    onChange({ ...state, amount: String(value) })
  }

  const handleConvertToChange = (option: ConvertToOption | undefined) => {
    if (option) {
      onChange({ ...state, convertTo: option.id })
    }
  }

  const handleAddressClick = () => {
    openSaleAddressModal({
      formType: addressDetails
        ? EditAddressButtons.edit
        : EditAddressButtons.add,
      fulfilmentDetails: addressDetails
        ? {
            type: FulfilmentType.DELIVERY,
            shippingAddress: addressDetails,
            billingAddress: state.billingAddressDetails || addressDetails,
            note: '',
          }
        : undefined,
      onSave: fulfilmentDetails => {
        const { shippingAddress: savedShipping, billingAddress: savedBilling } =
          fulfilmentDetails[0] || {}
        if (!savedShipping) {
          return
        }
        setAddressDetails(savedShipping)
        const hasDistinctBilling =
          !!savedBilling && !isSameAddress(savedBilling, savedShipping)
        onChange({
          ...state,
          deliveryAddress: formatLocalizedAddress(savedShipping),
          deliveryAddressDetails: savedShipping,
          billingAddress: hasDistinctBilling
            ? formatLocalizedAddress(savedBilling!)
            : undefined,
          billingAddressDetails: hasDistinctBilling ? savedBilling : undefined,
        })
      },
    })
  }

  const handleNoteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ ...state, note: e.target.value })
  }

  const requestedAmount = Number(state.amount) || 0
  const remainingBalance = Math.max(saleTotal - requestedAmount, 0)

  if (!showButton) {
    return null
  }

  return (
    <Card
      className="quote-payment-request-card"
      data-testid="quote-payment-request-card"
    >
      <Flag
        className="quote-payment-request-card__new-badge"
        content={<T _str="New" _context={NEW_CONTEXT} />}
        category="info"
      />
      <label className="quote-payment-request-card__toggle">
        <Switch
          small={true}
          checked={state.isToggleEnabled}
          onChange={handleToggle}
          data-testid="quote-payment-request-toggle"
        />
        <div className="quote-payment-request-card__toggle-label">
          <Text>
            {state.isShareLink ? (
              <T
                _str="Generate pay link for {name}"
                _context={TOGGLE_CONTEXT}
                name={
                  <strong>
                    {customer?.first_name || customer?.last_name
                      ? `${customer.first_name ?? ''} ${
                          customer.last_name ?? ''
                        }`.trim()
                      : state.email || customerEmail}
                  </strong>
                }
              />
            ) : state.email || customerEmail ? (
              <T
                _str="Request payment to {email}"
                _context={TOGGLE_CONTEXT}
                email={<strong>{state.email || customerEmail}</strong>}
              />
            ) : (
              <T _str="Request payment" _context={TOGGLE_CONTEXT} />
            )}
          </Text>
        </div>
        {state.isToggleEnabled && (
          <Button
            link
            className="quote-payment-request-card__settings-btn"
            onClick={async (e: React.MouseEvent) => {
              e.preventDefault()
              const retailerDetails = buildRetailerDetails({ retailer })
              try {
                await validateMerchantAccount(retailerDetails)
                setShowEditModal(true)
              } catch {
                ToastNotifications.negative(
                  t(
                    'Unable to process payment requests at the moment, merchant validation failed.',
                    {
                      _context: 'Payment request merchant validation error',
                    }
                  )
                )
              }
            }}
            data-testid="quote-payment-request-settings"
          >
            <Icon icon="fa-pencil" />
          </Button>
        )}
      </label>

      <PaymentRequestModalWrapper
        showPaymentRequestModal={showEditModal}
        setShowPaymentRequestModal={() => setShowEditModal(false)}
        amountTendered={state.amount}
        editMode={true}
        showShareableLink={true}
        defaultEmail={state.email}
        defaultNote={state.note}
        pricingTableTitle="Quote"
        onEditComplete={update => onChange({ ...state, ...update })}
      />

      {state.isToggleEnabled && (
        <div
          className="quote-payment-request-card__body"
          data-testid="quote-payment-request-body"
        >
          <div
            className={`quote-payment-request-card__amount-convert-section${
              isQuoteDeliveryEnabled
                ? ''
                : ' quote-payment-request-card__amount-convert-section--single'
            }`}
          >
            <div className="quote-payment-request-card__amount-column">
              <Text className="vd-mb2">
                <strong>
                  <T _str="Request amount" _context={AMOUNT_CONTEXT} />
                </strong>
              </Text>
              <CurrencyInputWithSymbol
                value={state.amount}
                onChange={handleAmountChange}
                max={saleTotal}
                min={minimumAmount}
                placeholder={saleTotal.toFixed(2)}
                onError={() => {}}
              />
              <Text
                type="supplementary"
                color="supplementary"
                className="vd-mt2"
              >
                <T
                  _str="Remaining balance on sale after payment: {amount}"
                  _context={AMOUNT_CONTEXT}
                  amount={<Currency value={remainingBalance} />}
                />
              </Text>
            </div>

            {isQuoteDeliveryEnabled && (
              <div className="quote-payment-request-card__convert-column">
                <Text className="vd-mb2">
                  <strong>
                    <T _str="Convert to" _context={CONVERT_TO_CONTEXT} />
                  </strong>
                </Text>
                <div data-testid="quote-payment-request-convert-to">
                  <Dropdown
                    placeholder="Select fulfillment type"
                    showSuggestionFooter={false}
                    value={
                      CONVERT_TO_OPTIONS.find(opt => opt.id === state.convertTo)
                        ?.id
                    }
                    items={CONVERT_TO_OPTIONS}
                    onSelectCallback={handleConvertToChange}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="quote-payment-request-card__info">
            <Icon
              icon="fa-info-circle"
              className="quote-payment-request-card__info-icon"
            />
            <Text type="supplementary">
              <T
                _str={`Once paid, the quote will be converted to an unfulfilled ${
                  isQuoteDeliveryEnabled && state.convertTo === 'delivery'
                    ? 'delivery'
                    : 'pickup'
                } sale.`}
                _context={INFO_CONTEXT}
              />
            </Text>
          </div>

          {isQuoteDeliveryEnabled && state.convertTo === 'delivery' && (
            <div className="quote-payment-request-card__address-section">
              <Text className="vd-mb2">
                <strong>
                  <T _str="Shipping & Delivery" _context={ADDRESS_CONTEXT} />
                </strong>
              </Text>
              <div>
                {state.deliveryAddress ? (
                  <>
                    <Text
                      type="body"
                      onClick={handleAddressClick}
                      data-testid="quote-payment-request-address-button"
                      style={{ cursor: 'pointer' }}
                    >
                      {state.deliveryAddress}
                      <Icon icon="fa-pencil" className="vd-ml2" />
                    </Text>
                    {state.billingAddress && (
                      <div>
                        <Text
                          type="body"
                          data-testid="quote-payment-request-billing-address"
                        >
                          Billing: {state.billingAddress}
                        </Text>
                      </div>
                    )}
                  </>
                ) : (
                  <Button
                    type="button"
                    modifier="text"
                    variant="supplementary"
                    onClick={handleAddressClick}
                    data-testid="quote-payment-request-address-button"
                  >
                    <Icon icon="plus" className="vd-mr1" />
                    <T _str="Add address" _context={ADDRESS_CONTEXT} />
                  </Button>
                )}
              </div>
            </div>
          )}

          <textarea
            className="quote-payment-request-card__note"
            data-testid="quote-payment-request-note"
            placeholder={t('Add note to payment request...', {
              _context: NOTE_CONTEXT,
            })}
            value={state.note}
            onChange={handleNoteChange}
            rows={3}
          />
        </div>
      )}
    </Card>
  )
}
